<script setup lang="ts">
import BaseAddress from '@/components/molecules/BaseAddress.vue';
import { useAppStore } from '@/store/app';
import { twMerge } from 'tailwind-merge';
import { inject } from 'vue';
import useAirdropChecker from './useAirdropChecker';

const appStore = useAppStore();

const {
  isLoading,
  maxAddresses,
  inputAddresses,
  inputErrorMessages,
  addressBookSelect,
  addressSelectItems,
  onTryDemo,
  onImport,
  onCheck,
} = inject('useAirdropChecker') as ReturnType<typeof useAirdropChecker>;
</script>

<template>
  <div class="max-w-[700px] self-center w-full flex flex-col gap-4">
    <div class="flex flex-col gap-2">
      <div class="d-flex mb-2">
        <v-select
          v-model="addressBookSelect"
          density="compact"
          :items="addressSelectItems"
          variant="solo-filled"
          flat
          :transition="false"
          hide-details
          :list-props="{
            density: 'compact',
          }"
          class="pa-0"
          placeholder="Import wallets from address book"
        >
          <template #item="{ item, props }">
            <v-divider v-if="item.raw.type === 'divider'"> </v-divider>
            <v-list-item
              v-else-if="item.raw.type === 'bundle'"
              density="compact"
              v-bind="props"
              :title="item.title"
            >
              <template #prepend>
                <v-avatar :rounded="0" size="1em">
                  <v-icon icon="mdi-package-variant-closed" size="1.2em" />
                </v-avatar>
              </template>
            </v-list-item>
            <v-list-item
              v-else-if="item.raw.type === 'address'"
              v-bind="props"
              :title="undefined"
              density="compact"
            >
              <template #prepend>
                <v-avatar
                  rounded="0"
                  :image="item.raw.image"
                  size="1em"
                ></v-avatar>
              </template>
              <v-list-item-title>
                <base-address
                  :address="item.raw.address"
                  :name="item.raw.name"
                  force-short
                ></base-address>
              </v-list-item-title>
            </v-list-item>
          </template>
          <template #selection="{ item }">
            <v-list-item
              v-if="item.raw.type === 'bundle'"
              density="compact"
              class="mx-0 pa-0 my-n2"
            >
              <template #prepend>
                <v-avatar :rounded="0" size="1em">
                  <v-icon icon="mdi-package-variant-closed" size="1.2em" />
                </v-avatar>
              </template>
              <v-list-item-title>
                {{ item.title }}
              </v-list-item-title>
            </v-list-item>
            <v-list-item v-else density="compact" class="mx-0 pa-0 my-n2">
              <template #prepend>
                <v-avatar
                  rounded="0"
                  :image="item.raw.image"
                  size="1em"
                ></v-avatar>
              </template>
              <v-list-item-title>
                <base-address
                  :address="item.raw.address"
                  :name="item.raw.name"
                  force-short
                ></base-address>
              </v-list-item-title>
            </v-list-item>
          </template>
        </v-select>
        <v-btn
          class="ml-2"
          height="40px"
          @click="onImport"
          :disabled="!addressBookSelect"
          >Import</v-btn
        >
      </div>

      <div class="relative">
        <v-textarea
          v-model="appStore.airdropCheckInput"
          density="compact"
          variant="solo-filled"
          placeholder="Or add it manually"
          flat
          auto-grow
          hide-details
          rows="7"
          v-on:update:focused="
            () => {
              inputErrorMessages = [];
            }
          "
          :error-messages="inputErrorMessages"
        ></v-textarea>

        <p
          :class="
            twMerge(
              'text-right text-disabled text-sm absolute bottom-0 right-0 p-2',
              inputAddresses.length > 0 ? 'opacity-100' : 'opacity-0',
            )
          "
        >
          {{ inputAddresses.length }}/{{ maxAddresses }} wallet{{
            inputAddresses.length > 1 ? 's' : ''
          }}
        </p>
      </div>
      <p v-if="inputErrorMessages.length > 0" class="text-red-300">
        {{ inputErrorMessages.join(', ') }}
      </p>
    </div>

    <div class="flex flex-col gap-2">
      <v-btn
        block
        color="primary"
        flat
        class="py-6"
        @click="onCheck"
        :loading="isLoading"
        :disabled="isLoading"
        >CHECK</v-btn
      >
      <p class="text-center" v-if="appStore.airdropCheckInput.length === 0">
        Want to give it a try?
        <button @click="onTryDemo" class="text-primary hover:underline">
          Check our demo
        </button>
      </p>
    </div>
  </div>
</template>
